// "Nearby" — the sheet listing the study spots, libraries, bathrooms and bus
// stops closest to you, and a detail view for whichever one you pick.
//
// It no longer carries a button of its own: the bottom-centre slot that held it
// is now the "get the app" button, and this sheet is opened by the host — from
// a tapped bus-stop pin, or a shared ?place=<id> link.
//
// Nearest *first* is the whole point: someone opening this wants somewhere to
// sit (or a washroom) now, not a directory. Distance is straight-line from the
// user's position — walking distance would need a route per row, far too much
// work for a list that redraws as you type — and with no position at all the
// list falls back to alphabetical rather than disappearing.
//
// Picking a row hands the place up to PathfinderTab, which flies the map to it,
// switches that building to the right floor and highlights its polygons. The
// detail view shows everything attached to it: every image, the amenity chips,
// a Send button and a Directions button that routes to it like any other room.
//
// The kind tabs are a horizontal scroller with no "All" — the three kinds
// answer genuinely different questions, and a mixed list buries study spots
// under the far more numerous washrooms.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  FILTERABLE_KINDS, PLACE_KINDS, SHOWN_KINDS, formatMeters, floorTag, hasActiveFilters,
  nearbyPlaces, placeChips, placeColor, placeIconName, placeKindKey, placeNote, placeTitle,
} from '../lib/places.js';
import PlaceIcon from './PlaceIcons.jsx';
import StarRating from './StarRating.jsx';
import BusStopDepartures from './BusStopDepartures.jsx';
import { stopKeyOf } from '../lib/transit.js';
import { defaultSheetHeight, dragHeight, sheetLimits, shouldDismiss } from '../lib/sheet.js';
import { compressAll, MAX_PHOTOS } from '../lib/photo.js';

// Ratings answer "is this a good place to sit and work?", so they're offered on
// the kinds that question makes sense for — and pointedly not on washrooms.
const RATABLE_KINDS = new Set(['study', 'library']);
const NO_RATING = { avg: 0, count: 0 };

// A bus stop's sheet opens at this much of the screen instead of as tall as it
// is allowed to go. Its content is ten departures over three days, so "as tall
// as allowed" means covering most of the map — and the map is half the answer
// here: the first two departures and seeing which kerb the stop is on is what
// somebody opened it for. The rest is a drag away.
const BUS_SHEET_FRACTION = 0.45;
// …and never less than this much, whatever the sheet has measured. The
// departures arrive from the network after the stop's sheet is already open, so
// the height would otherwise be chosen against a single "Loading departures…"
// line and the sheet would open as a strip. See defaultSheetHeight's floor.
const BUS_SHEET_MIN_FRACTION = 0.35;

export default function PlacesPanel({
  entries,          // attachPlaces() output
  origin,           // { lat, lng } | null — the user's position
  imageUrl,         // (rel) => url
  open,             // the host decides — nothing in here opens the sheet
  onClose,
  selected,         // the chosen entry | null
  onSelect,
  onGo,             // (entry) => route to it; omitted when it isn't routable
  onPhotoSubmit,    // async ({ placeId, …, images }) => void; omitted disables submitting
  ratings,          // { [placeId]: { avg, count } } — absent means nobody has rated
  onRate,           // async (placeId, stars, previous) => { avg, count }; omitted = read-only
  shareUrl,         // deep link that reopens this place on another device
  kind,             // active tab — owned by the host, because the map pins
  onKind,           // follow it
  filters,          // attribute filters; also host-owned, same reason
  onFilters,
  onLog,            // (event) => void — anonymous usage counts; omitted = silent
  // (stopKey) => Promise<{ stop, routes, departures }>. Bus times need a
  // server-side API key, so a host without one simply omits this and a bus
  // stop's sheet says times are unavailable rather than failing to open.
  loadDepartures,
}) {
  const [query, setQuery] = useState('');
  const [lightbox, setLightbox] = useState(null);   // { images, index }
  const listRef = useRef(null);

  const rows = useMemo(
    () => nearbyPlaces(entries, origin, { kind, query, filters }),
    [entries, origin, kind, query, filters],
  );

  // Back to the top when the filter changes.
  useEffect(() => { listRef.current?.scrollTo?.({ top: 0 }); }, [kind, query, filters]);

  // A filter combination that matches nothing says something real about campus
  // — no accessible washroom on this floor, no quiet spot with plugs — so it is
  // worth counting. Only while the list is actually on screen, and not for a
  // half-typed search box, which empties for a keystroke at a time.
  useEffect(() => {
    if (!open || selected || !onLog) return undefined;
    if (rows.length > 0 || !hasActiveFilters(filters)) return undefined;
    const t = setTimeout(() => onLog({ event: 'empty', kind, count: 0 }), 600);
    return () => clearTimeout(t);
  }, [open, selected, rows.length, kind, filters, onLog]);

  // ── Bottom-sheet height ───────────────────────────────────────────────────
  // Declared AFTER `rows`: the re-measure effect lists it as a dependency, and
  // dependency arrays are built during render — reading `rows` above its own
  // `const` puts it in the temporal dead zone ("Cannot access uninitialized
  // variable" in Safari, "before initialization" in Chrome).
  // The sheet is given a *definite* height rather than being sized by its
  // contents. That is what stops it jumping when a filter changes the number of
  // rows — the box stays put and only the list inside it changes — and it is
  // what makes the height draggable at all. Kept in px so the drag can be 1:1
  // with the finger.
  const sheetRef = useRef(null);
  const [height, setHeight] = useState(null);
  const [dragging, setDragging] = useState(false);
  const dragRef = useRef(null);

  // The sheet's natural height: everything above the scroller, plus that
  // scroller's *full* content, plus the bottom padding. Measured rather than
  // guessed, because it changes with every filter.
  const [contentHeight, setContentHeight] = useState(null);
  // The same measurement stopped after the FIRST row — the shortest the sheet
  // may be drawn. Measured, not guessed, because the furniture above the list
  // grows and shrinks with the filter row.
  const [oneRowHeight, setOneRowHeight] = useState(null);

  const measure = useCallback(() => {
    const sheet = sheetRef.current;
    if (!sheet) return;
    // The list view and the detail view each have their own scroller; whichever
    // is mounted is the one that can overflow.
    const scroller = sheet.querySelector('.pfa-places-list, .pfa-place-detail-body');
    const padBottom = parseFloat(getComputedStyle(sheet).paddingBottom) || 0;
    // Measure the INNER wrapper, never the scroller itself. The scroller is
    // `flex: 1 1 auto`, so it stretches to fill the sheet and its `scrollHeight`
    // reports `max(content, clientHeight)` — i.e. the sheet's own height once
    // the sheet is taller than the content. Measuring that is circular and the
    // sheet can never shrink, which is exactly the empty space below short
    // lists and short detail views. A plain block child reports its real height.
    const inner = scroller?.querySelector(':scope > .pfa-scroll-inner');
    const natural = inner
      ? scroller.offsetTop + inner.offsetHeight + padBottom
      : sheet.scrollHeight;
    setContentHeight(prev => (prev != null && Math.abs(prev - natural) < 2 ? prev : natural));

    // Furniture + one row. `.pfa-place-row` is the list view; in the detail
    // view there is no row to guarantee, so leave the previous floor alone.
    const firstRow = inner?.querySelector(':scope > .pfa-place-row, .pfa-place-row');
    if (firstRow && scroller) {
      const oneRow = scroller.offsetTop + firstRow.offsetHeight + padBottom;
      setOneRowHeight(prev => (prev != null && Math.abs(prev - oneRow) < 2 ? prev : oneRow));
    }
  }, []);

  const limits = useCallback(() => sheetLimits(
    sheetRef.current?.offsetParent?.clientHeight
      || (typeof window !== 'undefined' ? window.innerHeight : 800),
    contentHeight,
    oneRowHeight,
  ), [contentHeight, oneRowHeight]);

  // Re-measure whenever what's in the sheet changes. Images in a place's
  // gallery load late and change the height after the fact, so the scroller is
  // observed as well.
  useLayoutEffect(() => {
    if (!open) return undefined;
    measure();
    const inner = sheetRef.current?.querySelector('.pfa-scroll-inner');
    if (!inner || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(inner);
    return () => ro.disconnect();
  }, [open, measure, rows, selected, kind, filters, query]);

  // How tall this particular view wants to open — see BUS_SHEET_FRACTION. Null
  // for everything else, meaning "as tall as allowed".
  const sheetFraction = selected && stopKeyOf(selected.place) ? BUS_SHEET_FRACTION : null;

  // Switching between a list and a bus stop re-defaults the height rather than
  // inheriting it. Without this, opening a stop from the list would keep the
  // list's full-height sheet — and going back would leave the list stuck at the
  // stop's 45%, which reads as the sheet having shrunk on its own. Declared
  // above the sizing effect so its `null` lands first in the same batch, and
  // that effect (which lists sheetFraction) then computes the new default.
  const draggedRef = useRef(false);
  useLayoutEffect(() => { setHeight(null); draggedRef.current = false; }, [sheetFraction]);

  // Open as tall as this view wants, and afterwards keep whatever the user
  // dragged to — but never taller than the content now needs, so narrowing a
  // filter closes the gap instead of leaving blank space. The height transition
  // animates it.
  //
  // A stop's sheet keeps re-defaulting until the reader drags it, because its
  // content lands late: the departures arrive over the network a moment after
  // the sheet is open, and a height fixed against the "Loading…" line would
  // leave the timetable scrolling inside a strip. Once a finger has set a
  // height, that is the height — an answer arriving is not a reason to move a
  // sheet somebody has just sized.
  useLayoutEffect(() => {
    if (!open) return;
    const { min, max } = limits();
    const settled = prev => Math.round(Math.max(min, Math.min(max, prev)));
    setHeight(prev => (prev == null || (sheetFraction && !draggedRef.current)
      ? defaultSheetHeight(limits().host, contentHeight, oneRowHeight,
                           sheetFraction, sheetFraction ? BUS_SHEET_MIN_FRACTION : null)
      : settled(prev)));
  }, [open, limits, contentHeight, oneRowHeight, sheetFraction]);

  const onGrabDown = useCallback((e) => {
    const { min, max } = limits();
    dragRef.current = { startY: e.clientY, startHeight: height ?? min, raw: height ?? min, min, max };
    setDragging(true);
    e.currentTarget.setPointerCapture?.(e.pointerId);
  }, [height, limits]);

  const onGrabMove = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    const { raw, height: next } = dragHeight({ ...d, currentY: e.clientY });
    d.raw = raw;
    // From here the height is the reader's, not the view's — see the sizing
    // effect. Reset when the view itself changes.
    draggedRef.current = true;
    setHeight(next);
  }, []);

  const onGrabUp = useCallback((e) => {
    const d = dragRef.current;
    if (!d) return;
    dragRef.current = null;
    setDragging(false);
    // Throws InvalidPointerId if capture was never taken (some mouse paths).
    try { e.currentTarget.releasePointerCapture?.(e.pointerId); } catch { /* not captured */ }
    // Flick/drag well past the bottom stop dismisses, the way a sheet should.
    if (shouldDismiss(d.raw, d.min)) onClose();
  }, [onClose]);

  // Ask for the size about to be painted, not the camera original: the list
  // icon is 62x50 and was pulling a 3 MB photo per row, which is most of why
  // the panel felt slow to open.
  const resolve = (rel, variant) => (imageUrl ? imageUrl(rel, variant) : rel);

  if (!entries?.length) return null;

  // ⚠️ CLOSED, THIS DRAWS NOTHING. There used to be a "Nearby" button here, at
  // the bottom centre of the map; that corner now carries the "get the app"
  // button instead (see `appPromo` in PathfinderTab), because the places, the
  // filters and the photos are what the phone app is for and the web map's job
  // is to hand somebody over to it.
  //
  // The sheet itself is untouched and still opens from everything that opened
  // it before the button existed as the only way in: a tapped bus-stop pin, and
  // a shared ?place=<id> link. It is the host that decides whether `open` is
  // ever true, so a host that wants a button back needs only to add one.
  if (!open) return null;

  return (
    <>
      <div
        ref={sheetRef}
        className={`pfa-panel pfa-places${dragging ? ' dragging' : ''}`}
        style={height != null ? { height } : undefined}
      >
        <div
          className="pfa-sheet-grab"
          onPointerDown={onGrabDown}
          onPointerMove={onGrabMove}
          onPointerUp={onGrabUp}
          onPointerCancel={onGrabUp}
          role="separator"
          aria-label="Drag to resize"
        >
          <span className="pfa-sheet-bar" />
        </div>
        {selected ? (
          <PlaceDetail
            entry={selected}
            resolve={resolve}
            onBack={() => onSelect(null)}
            onClose={onClose}
            onGo={onGo}
            onPhotoSubmit={onPhotoSubmit}
            loadDepartures={loadDepartures}
            rating={ratings?.[selected.place.id] || NO_RATING}
            onRate={onRate}
            shareUrl={shareUrl}
            onOpenImage={(images, index) => setLightbox({ images, index })}
          />
        ) : (
          <>
            <div className="pfa-places-head">
              <strong>Nearby</strong>
              <button className="pfa-places-x" onClick={onClose} aria-label="Close">
                <PlaceIcon name="close" size={15} />
              </button>
            </div>

            <div className="pfa-places-filters">
              {SHOWN_KINDS.map(k => {
                const n = entries.filter(e => placeKindKey(e.place) === k).length;
                if (!n) return null;
                return (
                  <button key={k} className={`pfa-places-tab ${kind === k ? 'on' : ''}`}
                          onClick={() => onKind(k)}>
                    <PlaceIcon name={PLACE_KINDS[k].icon} size={15} />
                    {PLACE_KINDS[k].plural}
                    <span>{n}</span>
                  </button>
                );
              })}
            </div>

            <input
              className="pfa-places-search"
              placeholder="Filter by name, building or room…"
              value={query}
              onChange={e => setQuery(e.target.value)}
            />

            {FILTERABLE_KINDS.has(kind) && (
              <AttributeFilters kind={kind} filters={filters} onFilters={onFilters}
                                onLog={onLog} logKind={kind} />
            )}

            {!origin && (
              <div className="pfa-places-note">
                Turn on location to sort these by how close they are.
              </div>
            )}

            <div className="pfa-places-list" ref={listRef}>
              <div className="pfa-scroll-inner">
                {rows.length === 0 && <div className="pfa-places-empty">Nothing matches that.</div>}
                {rows.map(entry => (
                  <PlaceRow key={entry.place.id} entry={entry} resolve={resolve}
                            rating={ratings?.[entry.place.id] || NO_RATING}
                            onClick={() => onSelect(entry)} />
                ))}
              </div>
            </div>
          </>
        )}
      </div>

      {lightbox && (
        <Lightbox
          images={lightbox.images}
          index={lightbox.index}
          resolve={resolve}
          onIndex={(i) => setLightbox(lb => ({ ...lb, index: i }))}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
}

// Each control cycles through its states on tap rather than opening a menu —
// one thumb, one target, no dropdown to dismiss. `null` is always "no opinion",
// so a row of untouched controls filters nothing.
const CAPACITY_CYCLE = [
  { value: null,     label: 'All Capacities', icon: 'seat' },
  { value: 'low',    label: 'Few Seats',      icon: 'seat' },
  { value: 'medium', label: 'Medium Seats',   icon: 'seat' },
  { value: 'high',   label: 'Many Seats',     icon: 'seat' },
];
const ZONE_CYCLE = [
  { value: null,    label: 'Any Zone',   icon: 'quiet' },
  { value: 'quiet', label: 'Quiet Zone', icon: 'quiet' },
  { value: 'loud',  label: 'Loud Zone',  icon: 'loud' },
];
const TABLE_CYCLE = [
  { value: null,     label: 'Tables or Chairs', icon: 'table' },
  { value: 'tables', label: 'Has Tables',       icon: 'table' },
  { value: 'chairs', label: 'Chairs Only',      icon: 'chair' },
];

const GENDER_CYCLE = [
  { value: null,     label: 'Any Washroom', icon: 'bathroom-universal' },
  { value: 'mens',   label: "Men's",        icon: 'bathroom-mens' },
  { value: 'womens', label: "Women's",      icon: 'bathroom-womens' },
];

function AttributeFilters({ kind, filters, onFilters, onLog, logKind }) {
  // Count the control, never the value: "people filter for accessible" is the
  // useful fact, and it stays a count rather than a description of anyone.
  const note = (key) => onLog?.({ event: 'filter', kind: logKind, filter: key });
  const cycle = (key, states) => {
    const i = states.findIndex(s => s.value === filters[key]);
    onFilters({ ...filters, [key]: states[(i + 1) % states.length].value });
    note(key);
  };
  const state = (key, states) => states.find(s => s.value === filters[key]) || states[0];
  const toggle = (key) => { onFilters({ ...filters, [key]: !filters[key] }); note(key); };
  const clear = () => onFilters({
    capacity: null, plugs: false, zone: null, tables: null,
    gender: null, accessible: false, period: false,
  });

  // Each kind gets the controls that mean something for it. Sharing one row
  // would offer washroom searchers "Chairs Only", which is nonsense.
  const controls = kind === 'bathroom'
    ? (() => {
        const g = state('gender', GENDER_CYCLE);
        return [
          { key: 'gender', on: !!filters.gender, icon: g.icon, label: g.label,
            onClick: () => cycle('gender', GENDER_CYCLE),
            color: filters.gender ? undefined : undefined },
          { key: 'accessible', on: filters.accessible, icon: 'accessible',
            label: 'Accessible', onClick: () => toggle('accessible') },
          { key: 'period', on: filters.period, icon: 'period',
            label: 'Period Products', onClick: () => toggle('period') },
        ];
      })()
    : (() => {
        const cap = state('capacity', CAPACITY_CYCLE);
        const zone = state('zone', ZONE_CYCLE);
        const tab = state('tables', TABLE_CYCLE);
        return [
          { key: 'capacity', on: !!filters.capacity, icon: cap.icon, label: cap.label,
            onClick: () => cycle('capacity', CAPACITY_CYCLE) },
          { key: 'plugs', on: filters.plugs, icon: 'plug',
            label: filters.plugs ? 'Has Plugs' : 'Any Plugs', onClick: () => toggle('plugs') },
          { key: 'zone', on: !!filters.zone, icon: zone.icon, label: zone.label,
            onClick: () => cycle('zone', ZONE_CYCLE) },
          { key: 'tables', on: !!filters.tables, icon: tab.icon, label: tab.label,
            onClick: () => cycle('tables', TABLE_CYCLE) },
        ];
      })();

  return (
    <div className="pfa-places-attrs">
      {controls.map(c => (
        <button key={c.key} className={`pfa-attr ${c.on ? 'on' : ''}`} onClick={c.onClick}>
          <PlaceIcon name={c.icon} size={14} />{c.label}
        </button>
      ))}
      {hasActiveFilters(filters) && (
        <button className="pfa-attr clear" onClick={clear}>
          <PlaceIcon name="close" size={13} />Clear
        </button>
      )}
    </div>
  );
}

function PlaceRow({ entry, resolve, rating, onClick }) {
  const { place, loc, buildingName, meters } = entry;
  const chips = placeChips(place);
  // A referenced photo that isn't hosted (yet) must read as "no photo" rather
  // than a broken-image icon — the paths are authored in a different repo from
  // the one that serves them, so they can legitimately be ahead of the files.
  const [coverBad, setCoverBad] = useState(false);
  const cover = coverBad ? null : (place.images || [])[0];
  const colour = placeColor(place);
  return (
    <button className="pfa-place-row" onClick={onClick}>
      <span className="pfa-place-thumb" style={{ borderColor: colour }}>
        {cover
          ? <img src={resolve(cover, 'thumb')} alt="" loading="lazy" width="62" height="50"
                 decoding="async" onError={() => setCoverBad(true)} />
          : <span className="pfa-place-thumb-icon" style={{ color: colour }}>
              <PlaceIcon name={placeIconName(place)} size={22} />
            </span>}
        {(place.images?.length || 0) > 1 && (
          <span className="pfa-place-thumb-n">{place.images.length}</span>
        )}
        {/* Floor as a corner badge rather than a word in the subtitle — it is a
            one-glyph fact and the subtitle is the crowded line. Not for a bus
            stop, which is outdoors on a kerb: "FO" there is a data code, not a
            floor anybody can walk to. */}
        {place.type !== 'busstop' && (
          <span className="pfa-place-floor" style={{ background: colour }}>
            F{floorTag(loc.floor)}
          </span>
        )}
      </span>
      <span className="pfa-place-body">
        <span className="pfa-place-title">{placeTitle(place)}</span>
        {/* The note last: for a microwave pinned to a corridor there is no room
            number, and "Fletcher Argue" alone doesn't tell you where to look. */}
        <span className="pfa-place-sub">
          {[buildingName, place.roomNumber && `Room ${place.roomNumber}`, placeNote(place)]
            .filter(Boolean).join(' · ')}
        </span>
        {/* The stars share the chip line rather than claiming one of their own —
            the row is already three lines tall and this is a scan, not a page. */}
        {(chips.length > 0 || RATABLE_KINDS.has(place.type)) && (
          <span className="pfa-place-chips">
            {RATABLE_KINDS.has(place.type) && (
              <StarRating value={rating.avg} count={rating.count} size={12} />
            )}
            {chips.slice(0, 3).map(c => <Chip key={c.key} chip={c} />)}
            {chips.length > 3 && <span className="pfa-place-chip more">+{chips.length - 3}</span>}
          </span>
        )}
      </span>
      {meters != null && <span className="pfa-place-dist">{formatMeters(meters)}</span>}
    </button>
  );
}

function Chip({ chip }) {
  const style = chip.color
    ? { background: `${chip.color}1f`, color: chip.color }
    : undefined;
  return (
    <span className="pfa-place-chip" style={style}>
      {chip.icon && <PlaceIcon name={chip.icon} size={13} />}
      {chip.text}
    </span>
  );
}

function PlaceDetail({ entry, resolve, onBack, onClose, onGo, onPhotoSubmit, loadDepartures, rating, onRate, shareUrl, onOpenImage }) {
  const { place, loc, buildingName, meters } = entry;
  const images = place.images || [];
  const chips = placeChips(place);
  const colour = placeColor(place);
  const stopKey = stopKeyOf(place);
  const [toast, setToast] = useState(null);
  // `meters` is only set when the list was sorted against a known position, so
  // with location off there is simply no distance to show.
  const distance = meters != null ? formatMeters(meters) : '';

  // Copy the link rather than opening a share sheet — same choice the route
  // pill makes, and it works identically on desktop and iOS.
  const share = async () => {
    const url = shareUrl || window.location.href;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(url);
      } else {
        const ta = document.createElement('textarea');
        ta.value = url;
        ta.setAttribute('readonly', '');
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setToast('Link copied');
    } catch {
      setToast('Could not copy the link');
    }
    setTimeout(() => setToast(null), 2200);
  };

  return (
    <>
      <div className="pfa-places-head">
        <button className="pfa-places-back" onClick={onBack} aria-label="Back to the list">
          <PlaceIcon name="chevron-left" size={17} />
        </button>
        {/* Name and stars share the headline. The stars wrap under the name on a
            narrow phone rather than squeezing it — a title that truncates to
            make room for a rating has the priority backwards. */}
        <div className="pfa-place-headline">
          <strong>{placeTitle(place)}</strong>
          {RATABLE_KINDS.has(place.type) && (
            <RatingBlock place={place} rating={rating} onRate={onRate} />
          )}
        </div>
        <button className="pfa-place-sendbox" onClick={share}
                aria-label="Send this place" title="Copy a link to this place">
          <PlaceIcon name="share" size={17} />
        </button>
        <button className="pfa-places-x" onClick={onClose} aria-label="Close">
          <PlaceIcon name="close" size={15} />
        </button>
      </div>

      <div className="pfa-place-detail-body">
      <div className="pfa-scroll-inner">
      <div className="pfa-place-where">
        {/* A bus stop has no floor to be on — it is a pole on a kerb, and the
            "Floor O" this used to print was the outdoors code leaking through
            as if it were an answer. The pill still carries the kind, which is
            what it is for; it just stops claiming a storey. */}
        <span className="pfa-place-kind" style={{ background: `${colour}1f`, color: colour }}>
          <PlaceIcon name={placeIconName(place)} size={14} />
          {stopKey ? ' Bus stop' : ` Floor ${floorTag(loc.floor)}`}
        </span>
        {[buildingName, place.roomNumber && `Room ${place.roomNumber}`, placeNote(place), distance]
          .filter(Boolean).join(' · ')}
      </div>

      {chips.length > 0 && (
        <div className="pfa-place-chips detail">
          {chips.map(c => <Chip key={c.key} chip={c} />)}
        </div>
      )}


      {/* A bus stop's Directions button sits ABOVE its timetable, where every
          other place has it at the bottom. Ten departures spread over three
          days is a tall thing to scroll past, and "how do I walk there" was
          ending up below all of it — present, and unfindable. It is also the
          question you ask FIRST at a stop you have not been to before: the
          times only mean something once you know how long it takes to get
          there. */}
      {stopKey && (onGo
        ? (
          <button className="pfa-place-go bus" onClick={() => onGo(entry)}>
            Directions
          </button>
        )
        : (
          // Said rather than left as a missing button: about half the stops
          // have no walk-from node yet, and a sheet that silently omits
          // Directions reads as an app that forgot to draw it.
          <div className="pfa-places-note">
            No walking directions to this stop yet — it isn’t linked to a path on
            the map.
          </div>
        ))}

      {/* A bus stop's sheet leads with the timetable, because that is the only
          reason anybody opens one. It also takes no photo invite: a picture of
          a pole says nothing a passer-by needs, and the useful thing to know
          about a stop changes every few minutes. */}
      {stopKey ? (
        <BusStopDepartures
          stopKey={stopKey}
          cachedRoutes={(place.transit?.routes || []).map(n => ({ key: String(n), number: String(n) }))}
          loadDepartures={loadDepartures}
        />
      ) : images.length > 0 ? (
        <div className="pfa-place-gallery">
          {images.map((rel, i) => (
            <button key={rel} className="pfa-place-shot" onClick={() => onOpenImage(images, i)}>
              <img src={resolve(rel, 'card')} alt="" loading="lazy" decoding="async"
                   width="190" height="142"
                   onError={(e) => { e.target.closest('.pfa-place-shot').style.display = 'none'; }} />
            </button>
          ))}
        </div>
      ) : (
        // No photos: ask for one instead of just saying there aren't any. A
        // washroom is the one place we never invite photos of, for reasons that
        // need no explaining.
        onPhotoSubmit && place.type !== 'bathroom'
          ? <PhotoInvite entry={entry} onSubmit={onPhotoSubmit} />
          : <div className="pfa-places-note">No photos of this one yet.</div>
      )}

      {/* Everywhere else it stays where it has always been, under the gallery:
          you look at the place, then decide to go. A stop has its own copy
          above, so this must not draw a second one. */}
      {onGo && !stopKey && (
        <button className="pfa-place-go" onClick={() => onGo(entry)}>
          Directions
        </button>
      )}
      {toast && <div className="pfa-place-toast">{toast}</div>}
      </div>
      </div>
    </>
  );
}

// This browser's own rating of a place, so someone who mis-taps can correct it
// instead of the average counting them twice. Local only — the server is told
// what to amend, never who is amending it.
const MY_RATINGS_KEY = 'umaps-pf-my-ratings';

function readMyRating(placeId) {
  try { return JSON.parse(localStorage.getItem(MY_RATINGS_KEY) || '{}')[placeId] || null; }
  catch { return null; }
}
function writeMyRating(placeId, stars) {
  try {
    const all = JSON.parse(localStorage.getItem(MY_RATINGS_KEY) || '{}');
    all[placeId] = stars;
    localStorage.setItem(MY_RATINGS_KEY, JSON.stringify(all));
  } catch { /* private mode: the rating still counted, we just can't amend it */ }
}

/**
 * The rating beside a place's name: one small row of stars you can tap, with
 * the average after it.
 *
 * Always drawn — an unrated spot shows five empty stars and "No rating" rather
 * than hiding, because a place nobody has rated is exactly the one worth asking
 * about. The average comes from the server's response to your tap, so two
 * people rating at once can't drift apart.
 */
function RatingBlock({ place, rating, onRate }) {
  const [shown, setShown] = useState(rating);
  const [mine, setMine] = useState(() => readMyRating(place.id));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Moving to another place swaps everything this block is about.
  useEffect(() => {
    setShown(rating);
    setMine(readMyRating(place.id));
    setError('');
  }, [place.id, rating]);

  const submit = async (stars) => {
    if (busy || stars === mine) return;
    setBusy(true);
    setError('');
    try {
      const next = await onRate(place.id, stars, mine);
      writeMyRating(place.id, stars);
      setMine(stars);
      if (next) setShown({ avg: next.avg, count: next.count });
    } catch (err) {
      setError(err.message || "Couldn't save that rating.");
    }
    setBusy(false);
  };

  return (
    <div className="pfa-place-rating" title={error || undefined}>
      <StarRating
        value={shown.avg}
        count={shown.count}
        mine={mine}
        size={15}
        busy={busy}
        onRate={onRate ? submit : null}
      />
      {error && <span className="pfa-rating-err">{error}</span>}
    </div>
  );
}

/**
 * The empty gallery for a place nobody has photographed yet: a camera-plus tile
 * that asks the reader to be the one who does.
 *
 * Photos are picked with a plain file input rather than `capture`, so the OS
 * sheet still offers "Take Photo" *and* the library — someone who shot the room
 * an hour ago shouldn't have to walk back. They are downscaled in the browser
 * (lib/photo.js) and handed to the host, which queues them for moderation:
 * nothing here appears on the place until an admin accepts it, and the copy
 * says so, because a submission that silently vanishes reads as a broken app.
 */
function PhotoInvite({ entry, onSubmit }) {
  const { place, loc, buildingName } = entry;
  const inputRef = useRef(null);
  const [shots, setShots] = useState([]);      // data URLs, already downscaled
  const [busy, setBusy] = useState(false);     // reading files or uploading
  const [error, setError] = useState('');
  const [sent, setSent] = useState(0);         // photos accepted by the server so far
  const [progress, setProgress] = useState(null); // { done, total } while uploading
  // Reading files off the disk is quick; the upload is the part worth a banner.
  const uploading = !!progress;

  // A fresh place means a fresh form — the sheet reuses this component as you
  // move between spots.
  useEffect(() => { setShots([]); setError(''); setSent(0); }, [place.id]);

  const pick = () => { setError(''); inputRef.current?.click(); };

  const onFiles = async (e) => {
    // Copy the FileList into a real array FIRST. `input.files` is live: clearing
    // `value` empties the very object a reference is holding, so reading it
    // afterwards finds nothing and the whole submission silently does nothing.
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // let the same file be picked again after a removal
    if (!files.length) return;
    setBusy(true);
    try {
      const added = await compressAll(files);
      setShots(prev => [...prev, ...added].slice(0, MAX_PHOTOS));
      setError('');
    } catch (err) {
      setError(err.message || 'Could not read that photo.');
    }
    setBusy(false);
  };

  // One request per photo, deliberately. A serverless request body caps out at
  // 4.5 MB and four camera photos base64'd sail past it — which fails as a
  // non-JSON error the app can only report as a shrug. One at a time each
  // request is a few hundred KB, and a failure half way through keeps the
  // photos that haven't gone yet instead of losing the lot.
  const send = async () => {
    if (!shots.length) return;
    setBusy(true);
    setError('');
    const remaining = [...shots];
    let ok = 0;
    try {
      while (remaining.length) {
        setProgress({ done: ok, total: shots.length });
        await onSubmit({
          placeId: place.id,
          placeName: placeTitle(place),
          placeType: place.type,
          building: buildingName,
          roomNumber: place.roomNumber || '',
          floor: loc.floor,
          images: [remaining[0]],
        });
        remaining.shift();
        ok += 1;
      }
      setShots([]);
    } catch (err) {
      setShots(remaining);   // keep what hasn't landed so Send can be retried
      setError(err.message || "That didn't send. Try again in a moment.");
    }
    // Confirm whatever did land, then hand the form straight back: someone who
    // photographed the room from the door usually has another from the window,
    // and making them reopen the place to send it is how the second gets lost.
    if (ok) setSent(n => n + ok);
    setProgress(null);
    setBusy(false);
  };

  return (
    <div className="pfa-photo-invite">
      {/* Uploading and sent are separate, always-visible banners rather than a
          word on the button. An upload is the one moment the app is doing
          something the user can't see, and "did that work?" is the question a
          submission form has to answer without being asked. */}
      {uploading && (
        <div className="pfa-photo-status uploading" role="status">
          <span className="pfa-photo-spinner" aria-hidden="true" />
          <span>
            <strong>
              {progress && progress.total > 1
                ? `Uploading photo ${progress.done + 1} of ${progress.total}…`
                : 'Uploading your photo…'}
            </strong>
            <br />Hang on — this can take a moment on campus wifi.
          </span>
        </div>
      )}

      {sent > 0 && !uploading && (
        <div className="pfa-photo-status sent" role="status">
          <span className="pfa-photo-sent-tick" aria-hidden="true">✓</span>
          <span>
            <strong>{sent === 1 ? 'Photo sent — thank you!' : `${sent} photos sent — thank you!`}</strong>
            <br />We review every photo by hand before it appears on this spot, so
            give it a day or two to show up.
          </span>
        </div>
      )}
      <input
        ref={inputRef}
        className="pfa-photo-file"
        type="file"
        // HEIC needs naming twice: iOS reports it as image/heic (covered by
        // image/*), but a desktop file dialog filtering on MIME type alone
        // greys the files out, so the extensions are listed as well.
        accept="image/*,.heic,.HEIC,.heif,.HEIF,.jpg,.jpeg,.png"
        multiple
        onChange={onFiles}
      />

      {shots.length === 0 ? (
        <button className={`pfa-photo-drop${sent > 0 ? ' compact' : ''}`} onClick={pick} disabled={busy}>
          <span className="pfa-photo-drop-icon"><PlaceIcon name="camera" size={sent > 0 ? 22 : 30} /></span>
          <span className="pfa-photo-drop-title">
            {busy ? 'Reading your photos…' : sent > 0 ? 'Submit another photo' : 'Submit photos of this spot'}
          </span>
          {sent > 0 && <span className="pfa-photo-drop-sub">Got another angle? Send it too.</span>}
          {sent === 0 && (
            <span className="pfa-photo-drop-sub">
              No one has photographed this one yet — help your fellow students find it.
            </span>
          )}
        </button>
      ) : (
        <>
          <div className="pfa-photo-picks">
            {shots.map((src, i) => (
              <div key={src.slice(-32) + i} className="pfa-photo-pick">
                <img src={src} alt="" />
                <button
                  className="pfa-photo-pick-x"
                  onClick={() => setShots(s => s.filter((_, j) => j !== i))}
                  aria-label="Remove this photo"
                  disabled={busy}
                ><PlaceIcon name="close" size={12} /></button>
              </div>
            ))}
            {shots.length < MAX_PHOTOS && (
              <button className="pfa-photo-pick add" onClick={pick} disabled={busy}
                      aria-label="Add another photo">
                <PlaceIcon name="plus" size={20} />
              </button>
            )}
          </div>
          <div className="pfa-photo-actions">
            <button className="pfa-photo-cancel" onClick={() => setShots([])} disabled={busy}>
              Cancel
            </button>
            <button className="pfa-photo-send" onClick={send} disabled={busy}>
              {uploading
                ? 'Uploading…'
                : busy
                  ? 'Reading…'
                  : `Send ${shots.length} photo${shots.length > 1 ? 's' : ''}`}
            </button>
          </div>
          <div className="pfa-photo-note">
            A moderator checks each photo before it appears on this spot.
          </div>
        </>
      )}

      {error && <div className="pfa-photo-err">{error}</div>}
    </div>
  );
}

/** Full-screen image viewer with arrows to step through the place's photos. */
function Lightbox({ images, index, resolve, onIndex, onClose }) {
  const many = images.length > 1;
  const step = useCallback((delta) => {
    onIndex((index + delta + images.length) % images.length);
  }, [index, images.length, onIndex]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      else if (many && e.key === 'ArrowRight') step(1);
      else if (many && e.key === 'ArrowLeft') step(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [many, step, onClose]);

  // Taps on the backdrop close; the image and controls stop that propagating.
  return (
    <div className="pfa-places-lightbox" onClick={onClose}>
      {many && (
        <button className="pfa-lb-arrow left"
                onClick={e => { e.stopPropagation(); step(-1); }} aria-label="Previous photo">
          <PlaceIcon name="chevron-left" size={26} />
        </button>
      )}
      <img src={resolve(images[index], 'full')} alt="" onClick={e => e.stopPropagation()} />
      {many && (
        <button className="pfa-lb-arrow right"
                onClick={e => { e.stopPropagation(); step(1); }} aria-label="Next photo">
          <PlaceIcon name="chevron-right" size={26} />
        </button>
      )}
      <button className="pfa-lb-close" onClick={onClose} aria-label="Close">
        <PlaceIcon name="close" size={18} />
      </button>
      {many && <div className="pfa-lb-count">{index + 1} / {images.length}</div>}
    </div>
  );
}
